import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, CheckCircle, Send, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { normalizeUrl, normalizeUrlFields } from "@/lib/urlUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPE_OPTIONS = ["Goods", "Services", "Food & Beverage", "Nonprofits"];
const BUSINESS_SUBCATEGORY_OPTIONS = {
  Goods: ["Art", "Books", "Candles", "Clothing", "Home Goods", "Jewelry", "Plants", "Skincare", "Vintage"],
  Services: ["Beauty", "Creative Services", "Education", "Event Services", "Fitness & Wellness", "Home Services", "Pet Services", "Photography", "Professional Services", "Repair Services"],
  "Food & Beverage": ["Bakery", "Beverages", "Catering", "Coffee & Tea", "Desserts", "Food Trucks", "Prepared Foods", "Specialty Foods", "Snacks"],
  Nonprofits: ["Chambers of Commerce"],
};
const LOCATION_OPTIONS = ["Clintonville", "Columbus", "East Market", "German Village", "Hilliard", "Linden", "Reynoldsburg", "Short North", "Westerville", "Worthington", "Outside of the Greater Columbus Area", "Other"];

const resolveLocationValue = (location, otherLocation) => (
  location === "Other" ? String(otherLocation || "").trim() : location
);

const initialForm = {
  business_name: "",
  business_email: "",
  market_name: "",
  market_email: "",
  website: "",
  business_type: "",
  business_categories: [],
  business_category_other: "",
  business_description: "",
  location: "",
  location_other: "",
};

export const Join614Signup = ({ token }) => {
  const [signupType, setSignupType] = useState("business");
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const isBusiness = signupType === "business";
  const subcategories = form.business_type ? BUSINESS_SUBCATEGORY_OPTIONS[form.business_type] || [] : [];

  const setField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  const handleLocationChange = (value) => {
    setForm((previous) => ({
      ...previous,
      location: value,
      location_other: value === "Other" ? previous.location_other : "",
    }));
  };

  const handleBusinessTypeChange = (value) => {
    setForm((previous) => ({
      ...previous,
      business_type: value,
      business_categories: [],
      business_category_other: "",
    }));
  };

  const toggleCategory = (category) => {
    setForm((previous) => {
      const current = previous.business_categories || [];
      const next = current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category];
      return {
        ...previous,
        business_categories: next,
        business_category_other: next.includes("Other") ? previous.business_category_other : "",
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isBusiness && (!form.business_name.trim() || !form.business_email.trim())) {
      toast.error("Business name and email are required");
      return;
    }
    if (!isBusiness && (!form.market_name.trim() || !form.market_email.trim())) {
      toast.error("Market name and email are required");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isBusiness ? `${API}/admin/business/quick-signup` : `${API}/admin/market/quick-signup`;
      const resolvedLocation = resolveLocationValue(form.location, form.location_other);
      const payload = normalizeUrlFields(isBusiness
        ? { ...form, location: resolvedLocation, business_locations: resolvedLocation ? [resolvedLocation] : [] }
        : {
            market_name: form.market_name,
            email: form.market_email,
            website: form.website,
            description: form.business_description,
            location: resolvedLocation,
          });
      const response = await axios.post(endpoint, payload, { headers });
      const label = isBusiness ? "Business" : "Market";
      toast.success(response.data.email_sent ? `${label} signed up and email sent` : `${label} signed up, but email did not send`);
      setForm(initialForm);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to sign up ${isBusiness ? "business" : "market"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#42D78A]/10">
          {isBusiness ? <Building2 className="h-7 w-7 text-[#1a1a1a]" /> : <Store className="h-7 w-7 text-[#1a1a1a]" />}
        </div>
        <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Join 614 Shop Local</h1>
        <p className="mt-2 text-sm text-gray-500">A simplified admin signup form for in-person registration.</p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-6 grid grid-cols-2 gap-2 rounded-full bg-gray-100 p-1">
          {[
            { key: "business", label: "Business", icon: Building2 },
            { key: "market", label: "Market", icon: Store },
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSignupType(option.key)}
              className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                signupType === option.key ? "bg-[#1a1a1a] text-white" : "text-gray-600 hover:text-[#1a1a1a]"
              }`}
              data-testid={`quick-signup-type-${option.key}`}
            >
              <option.icon className="h-4 w-4" />
              {option.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-5">
          {isBusiness ? (
            <>
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={form.business_name}
                  onChange={(event) => setField("business_name", event.target.value)}
                  className="mt-2 h-12"
                  data-testid="quick-signup-business-name"
                />
              </div>
              <div>
                <Label>Business Email *</Label>
                <Input
                  type="email"
                  value={form.business_email}
                  onChange={(event) => setField("business_email", event.target.value)}
                  className="mt-2 h-12"
                  data-testid="quick-signup-business-email"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>Market Name *</Label>
                <Input
                  value={form.market_name}
                  onChange={(event) => setField("market_name", event.target.value)}
                  className="mt-2 h-12"
                  data-testid="quick-signup-market-name"
                />
              </div>
              <div>
                <Label>Market Email *</Label>
                <Input
                  type="email"
                  value={form.market_email}
                  onChange={(event) => setField("market_email", event.target.value)}
                  className="mt-2 h-12"
                  data-testid="quick-signup-market-email"
                />
              </div>
            </>
          )}
          <div>
            <Label>{isBusiness ? "Business" : "Market"} Website</Label>
            <Input
              type="url"
              value={form.website}
              onChange={(event) => setField("website", event.target.value)}
              onBlur={(event) => setField("website", normalizeUrl(event.target.value))}
              placeholder="https://"
              className="mt-2 h-12"
              data-testid="quick-signup-website"
            />
          </div>
          <div>
            <Label>City / Region</Label>
            <Select value={form.location} onValueChange={handleLocationChange}>
              <SelectTrigger className="mt-2 h-12" data-testid="quick-signup-location">
                <SelectValue placeholder="Select a city or region" />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_OPTIONS.map((location) => <SelectItem key={location} value={location}>{location}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.location === "Other" && (
              <Input
                value={form.location_other}
                onChange={(event) => setField("location_other", event.target.value)}
                placeholder="Enter city or region"
                className="mt-3 h-12"
                data-testid="quick-signup-location-other"
              />
            )}
          </div>
          {isBusiness && (
            <>
              <div>
                <Label>Main Category</Label>
                <Select value={form.business_type} onValueChange={handleBusinessTypeChange}>
                  <SelectTrigger className="mt-2 h-12" data-testid="quick-signup-main-category">
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPE_OPTIONS.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categories</Label>
                <div className="mt-2 rounded-xl border border-gray-200 p-3">
                  {!form.business_type ? (
                    <p className="text-sm text-gray-400">Choose a main category first.</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[...subcategories, "Other"].map((category) => (
                        <label key={category} className="flex items-center gap-2 text-sm text-gray-600">
                          <Checkbox
                            checked={form.business_categories.includes(category)}
                            onCheckedChange={() => toggleCategory(category)}
                            data-testid={`quick-signup-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          />
                          {category}
                        </label>
                      ))}
                    </div>
                  )}
                  {form.business_categories.includes("Other") && (
                    <Input
                      value={form.business_category_other}
                      onChange={(event) => setField("business_category_other", event.target.value)}
                      placeholder="Add another category"
                      className="mt-3 h-11"
                      data-testid="quick-signup-category-other"
                    />
                  )}
                </div>
              </div>
            </>
          )}
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.business_description}
              onChange={(event) => setField("business_description", event.target.value)}
              className="mt-2 min-h-[130px]"
              data-testid="quick-signup-description"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="mt-6 h-12 w-full rounded-full bg-[#42D78A] font-bold text-[#1a1a1a] hover:bg-[#3bc27c]"
          data-testid="quick-signup-submit"
        >
          {saving ? (
            <>
              <Send className="mr-2 h-4 w-4 animate-pulse" />
              Signing Up...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Sign Up {isBusiness ? "Business" : "Market"}
            </>
          )}
        </Button>
      </form>
    </div>
  );
};
